import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function ConfigureRouterPage(): Node {
  return html`
    <div>
      <h1>configureRouter</h1>
      <p>Configure the router's navigation strategy. By default, onefold uses <strong>path-based routing</strong> (History API). Use <code>configureRouter</code> to switch to hash-based routing for static hosting environments.</p>

      <h2>Routing Modes</h2>
      <table>
        <tr><th>Mode</th><th>URL Format</th><th>When to Use</th></tr>
        <tr><td><strong>Path</strong> (default)</td><td><code>/about</code>, <code>/users/42</code></td><td>Servers with SPA fallback (Express, Nginx, Vercel, Netlify with <code>_redirects</code>)</td></tr>
        <tr><td><strong>Hash</strong></td><td><code>#/about</code>, <code>#/users/42</code></td><td>Static hosting without server config (GitHub Pages, S3, local <code>file://</code>)</td></tr>
      </table>

      <h2>Default Behavior</h2>
      <p>Without calling <code>configureRouter</code>, the router uses path-based routing. It automatically falls back to hash mode when running on the <code>file:</code> protocol (e.g. opening an HTML file directly in the browser).</p>

      ${CodeBlock(`import { Router, navigate } from 'onefold';

// Path-based by default — no configuration needed
const app = Router([
  { path: '/', view: () => Home() },
  { path: '/about', view: () => About() },
], () => NotFound());

navigate('/about'); // URL becomes: /about`)}

      <h2>Enabling Hash Mode</h2>
      <p>Call <code>configureRouter({ hash: true })</code> <strong>before</strong> creating any Router or calling <code>navigate()</code>.</p>

      ${CodeBlock(`import { configureRouter, Router, navigate } from 'onefold';

// Enable hash routing for GitHub Pages / static hosting
configureRouter({ hash: true });

const app = Router([
  { path: '/', view: () => Home() },
  { path: '/about', view: () => About() },
], () => NotFound());

navigate('/about'); // URL becomes: #/about`)}

      ${Callout('configureRouter must be called before any Router or navigate call. Once the router initializes, changing the mode has no effect.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>configureRouter</code></td><td><code>{ hash?: boolean }</code></td><td>Set the routing strategy. <code>hash: true</code> enables hash-based routing.</td></tr>
      </table>

      <h2>How Each Mode Works</h2>

      <h3>Path Mode (default)</h3>
      <ul>
        <li>Uses <code>history.pushState()</code> to update the URL</li>
        <li>Listens to <code>popstate</code> events for back/forward navigation</li>
        <li>Requires the server to serve <code>index.html</code> for all routes (SPA fallback)</li>
        <li>Clean URLs: <code>/users/42</code></li>
      </ul>

      <h3>Hash Mode</h3>
      <ul>
        <li>Uses <code>window.location.hash</code> to store the route</li>
        <li>Listens to <code>hashchange</code> events for navigation</li>
        <li>Works on any static file server — no server config needed</li>
        <li>URLs include <code>#</code>: <code>example.com/#/users/42</code></li>
      </ul>

      <h2>Deployment Guide</h2>

      <h3>GitHub Pages</h3>
      ${CodeBlock(`// main.ts
import { configureRouter, Router, mount } from 'onefold';

configureRouter({ hash: true });

const app = Router([...routes], () => NotFound());
mount(app, document.getElementById('app')!);`)}
      <p>Also ensure your <code>index.html</code> uses relative paths for assets:</p>
      ${CodeBlock(`<!-- Use ./ instead of / for asset paths -->
<link rel="stylesheet" href="./style.css" />
<script type="module" src="./app.js"></script>`, 'html')}

      <h3>Intercepting Internal Links (Hash Mode)</h3>
      ${raw('<p>When using hash mode, any raw <code>&lt;a href="/..."&gt;</code> links in your page content will trigger a full page navigation instead of client-side routing. To fix this, add a global click interceptor after mounting your app:</p>')}
      ${CodeBlock(`import { configureRouter, Router, navigate, mount } from 'onefold';

configureRouter({ hash: true });

const app = Router([...routes], () => NotFound());
mount(app, document.getElementById('app')!);

// Intercept internal links so raw <a href="/..."> in page content
// uses client-side navigation instead of full page reload
document.addEventListener('click', (e) => {
  const anchor = (e.target as HTMLElement).closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) return;
  e.preventDefault();
  navigate(href);
});

// Navigate to home if no hash is present on initial load
if (!location.hash || location.hash === '#/') {
  navigate('/');
}`)}
      ${Callout('This interceptor is only needed if your page content contains plain <a href="/path"> links that are not using the Link component. If all navigation uses Link or navigate(), you can skip this.')}


      <h3>Server with SPA Fallback (Nginx)</h3>
      ${CodeBlock(`# No configureRouter needed — path mode works out of the box
# nginx.conf
location / {
  try_files $uri $uri/ /index.html;
}`, 'nginx')}

      <h3>Vercel / Netlify</h3>
      <p>These platforms support SPA fallback natively. Use path mode (the default) with no extra config.</p>

      <h2>Link Behavior</h2>
      <p>The <code>Link</code> component automatically adapts to the configured mode:</p>
      ${CodeBlock(`import { Link } from 'onefold';

// Path mode → <a href="/about">About</a>
// Hash mode → <a href="#/about">About</a>
Link('/about', 'About');`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/router">Router</a> — route definitions and matching</li>
        <li><a href="/routing/navigate">Navigate</a> — programmatic navigation</li>
        <li><a href="/routing/link">Link</a> — declarative navigation with active state</li>
        <li><a href="/routing/nested">Nested Routes</a> — layouts with child routes</li>
      </ul>
    </div>
  `;
}
